import { useQuery } from "@apollo/client";
import type {
  Organization,
  OrganizationMembersConnection,
  OrganizationMember,
} from "@casey/db-types";
import { GET_USER_ORGANIZATIONS } from "../gql";

interface GetUserOrganizationsVariables {
  userId: string;
}

interface GetUserOrganizationsResponse {
  organizationMembers: OrganizationMembersConnection;
}

export type UserOrganizationMembership = Pick<
  OrganizationMember,
  | "role"
  | "canWriteOrganization"
  | "canWriteMembers"
  | "canWriteProjects"
  | "canWriteOfferings"
  | "canWriteTemplates"
  | "canWriteInvites"
  | "canWriteSessions"
  | "canWriteAssets"
> & {
  organization: Organization;
};

export function useUserOrganizations(
  userId: string,
  options?: {
    includeArchived?: boolean;
    search?: string;
  },
) {
  const includeArchived = options?.includeArchived ?? false;
  const search = options?.search?.toLowerCase().trim();

  const { data, loading, error, refetch } = useQuery<
    GetUserOrganizationsResponse,
    GetUserOrganizationsVariables
  >(GET_USER_ORGANIZATIONS, {
    variables: { userId },
    skip: !userId,
    errorPolicy: "all",
  });

  const organizationMemberships: UserOrganizationMembership[] =
    data?.organizationMembers?.nodes
      ?.map((membership) => ({
        organization: membership?.organization!,
        role: membership?.role!,
        canWriteOrganization: membership?.canWriteOrganization ?? false,
        canWriteMembers: membership?.canWriteMembers ?? false,
        canWriteProjects: membership?.canWriteProjects ?? false,
        canWriteOfferings: membership?.canWriteOfferings ?? false,
        canWriteTemplates: membership?.canWriteTemplates ?? false,
        canWriteInvites: membership?.canWriteInvites ?? false,
        canWriteSessions: membership?.canWriteSessions ?? false,
        canWriteAssets: membership?.canWriteAssets ?? false,
      }))
      .filter((membership) => {
        if (!membership.organization) return false;
        // If includeArchived is false (default), filter out archived organizations
        if (!includeArchived && membership.organization.archivedAt)
          return false;

        // Apply search filter (client-side)
        if (search) {
          const name = membership.organization.name?.toLowerCase() || "";
          const slug = membership.organization.slug?.toLowerCase() || "";
          const description =
            membership.organization.description?.toLowerCase() || "";

          if (
            !name.includes(search) &&
            !slug.includes(search) &&
            !description.includes(search)
          ) {
            return false;
          }
        }

        return true;
      }) || [];

  const organizations = organizationMemberships.map(
    (membership) => membership.organization,
  );

  return {
    organizations,
    organizationMemberships,
    loading,
    error,
    refetch,
  };
}

export type { Organization };
